import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useApi, put, Panel, PageHead, Stat, Tabs, StatusBadge } from "@/crm/ui";

export default function RolesPermissions() {
  const catalog = useApi("/crm/permissions");
  const [role, setRole] = useState("manager");
  const [granted, setGranted] = useState([]);
  const d = catalog.data;
  const current = (d?.roles || []).find((r) => r.role === role);

  useEffect(() => { setGranted(current?.permissions || []); }, [role, current?.permissions]);

  const toggle = (perm) => setGranted((g) => (g.includes(perm) ? g.filter((p) => p !== perm) : [...g, perm]));
  const toggleModule = (m, actions) => {
    const all = actions.map((a) => `${m}:${a}`);
    const has = all.every((p) => granted.includes(p));
    setGranted((g) => (has ? g.filter((p) => !all.includes(p)) : [...new Set([...g, ...all])]));
  };

  if (!d) return <p className="text-sm text-muted-foreground" data-testid="roles-loading">Loading permissions…</p>;

  return (
    <div className="space-y-6" data-testid="roles-page">
      <PageHead eyebrow="Administration" title="Roles & permissions"
                subtitle="Granular module × action permissions per role. Every change is written to the immutable audit trail. The Administrator role always keeps full access."
                actions={<Button data-testid="save-permissions-btn"
                                 onClick={async () => {
                                   const r = await put(`/crm/roles/${role}`, { permissions: granted },
                                     `Permissions updated for ${role}`);
                                   if (r) catalog.reload();
                                 }}>Save permissions</Button>} />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Roles" value={(d.roles || []).length} testid="roles-count" />
        <Stat label="Modules" value={d.modules.length} testid="modules-count" />
        <Stat label="Actions" value={d.actions.length} testid="actions-count" />
        <Stat label={`Granted to ${role}`} value={granted.length} testid="granted-count" />
      </div>
      <Tabs tabs={(d.roles || []).map((r) => r.role)} active={role} onChange={setRole}
            testid="role-tabs" />
      <Panel title={`${current?.label || role} — data scope: ${current?.data_scope || "—"}`}
             testid="permission-matrix-panel"
             aside={<StatusBadge value={current?.system ? "System role" : "Custom role"} />}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] text-left text-xs" data-testid="permission-matrix">
            <thead className="border-b border-border">
              <tr>
                <th className="p-2 font-semibold">Module</th>
                {d.actions.map((a) => <th key={a} className="p-2 font-semibold">{a}</th>)}
              </tr>
            </thead>
            <tbody>
              {d.modules.map((m) => (
                <tr key={m} className="border-b border-border/60 hover:bg-muted/40">
                  <td className="p-2">
                    <button className="font-semibold text-primary" data-testid={`module-toggle-${m}`}
                            onClick={() => toggleModule(m, d.actions)}>{m}</button>
                  </td>
                  {d.actions.map((a) => {
                    const perm = `${m}:${a}`;
                    return (
                      <td key={a} className="p-2">
                        <input type="checkbox" checked={granted.includes(perm)}
                               disabled={role === "admin"}
                               data-testid={`perm-${m}-${a}`.replace(/\s+/g, "-").toLowerCase()}
                               onChange={() => toggle(perm)} />
                      </td>);
                  })}
                </tr>))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
